import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import { formatDateTime, formatDate, formatTimeOnly } from '../utils/dateHelpers';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Repeat as RepeatIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CreditCard as CreditCardIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { ridesAPI, driversAPI, dashboardAPI, carTypesAPI, accountsAPI } from '../services/apiService';
import { getRideStatus, getRideStatusColor, getRideStatusStyle, getDriverStatusColor } from '../utils/Status';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useSettings } from '../contexts/SettingsContext';
import { safeInvoke } from '../utils/signalRHelper';
import { formatPhoneNumber, DAYS_OF_WEEK_LONG, PRICING_DEFAULTS } from '../config/appConstants';

const DetailView = ({
  isAdmin = false,
  itemType,
  itemId,
  onBackToList,
  metricType,
  unreviewedRideIds = [],
  onReviewAndNext,
  onBackToDashboard
}) => {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('');
  const [reviewingRide, setReviewingRide] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const { signalRConnection, connectSignalR } = useAuth();
  const { showToast, showAlert } = useAlert();
  const [carTypeNames, setCarTypeNames] = useState({});
  const [pricingBreakdown, setPricingBreakdown] = useState(null);
  const [rideAccount, setRideAccount] = useState(null);
  const { settings, addOns } = useSettings();

  // (formatPhoneNumber imported from appConstants)

  useEffect(() => {
    loadItemDetails();
    // Load car types for dynamic name lookup
    carTypesAPI.getAll().then(types => {
      const nameMap = {};
      (types || []).forEach(t => { nameMap[t.id] = t.name; });
      setCarTypeNames(nameMap);
    }).catch(() => { });
  }, [itemType, itemId]);

  const loadItemDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      let data;
      if (itemType === 'ride') {
        data = await ridesAPI.getById(itemId);
        const rideStatus = getRideStatus(data);
        setStatus(rideStatus);
        // Load linked account for discount display
        if (data?.accountId) {
          accountsAPI.getById(data.accountId)
            .then(acc => setRideAccount(acc))
            .catch(() => setRideAccount(null));
        } else {
          setRideAccount(null);
        }
        // Fetch pricing breakdown for detailed breakdown display (best-effort, non-blocking)
        if (data?.route?.pickup && data?.route?.dropOff) {
          const stops = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
            .map(n => data.route[`stop${n}`])
            .filter(Boolean);
          ridesAPI.calculatePrice({
            pickup: data.route.pickup,
            dropOff: data.route.dropOff,
            stops,
            carType: data.carType || 1,
            isRoundTrip: data.route.roundTrip || false,
            scheduledTime: data.scheduledFor || null,
          }).then(result => {
            if (result?.success) setPricingBreakdown(result);
          }).catch(() => { });
        }
      } else if (itemType === 'driver') {
        data = await driversAPI.getById(itemId);
        const driverStatus = await driversAPI.getDriverStatus(data.id);
        setStatus(driverStatus);
      }
      setItem(data);
    } catch (err) {
      console.error(`Failed to load ${itemType} details:`, err);
      setError(`Failed to load ${itemType} details`);
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyDriverToAddCar = async () => {
    try {
      setSendingMessage(true);
      const result = await safeInvoke({
        connection: signalRConnection,
        connectFn: connectSignalR,
        methodName: 'DispatcherSendsMessage',
        args: [{
          DriverId: item.id,
          Message: 'Please add a vehicle to your account in order to view and accept open calls. Go to Settings > Car Management to add your vehicle information.',
        }],
        maxRetries: 0, // message sends are non-idempotent
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      showToast('Driver was notified to add a vehicle', 'success')
    } catch (err) {
      console.error('Failed to send message:', err);
      showToast('Failed to send notification', 'error')
    } finally {
      setSendingMessage(false);
    }
  };

  // Review mode helpers
  const isReviewMode = metricType === 'completedUnreviewedRides' && itemType === 'ride';
  const currentIndex = unreviewedRideIds.indexOf(parseInt(itemId)) + 1;
  const totalUnreviewed = unreviewedRideIds.length;

  const handleReviewAndNext = async () => {
    try {
      setReviewingRide(true);
      await dashboardAPI.markRideAsReviewed(parseInt(itemId));
      showToast('Ride marked as reviewed', 'success');
      if (onReviewAndNext) {
        onReviewAndNext(parseInt(itemId));
      }
    } catch (err) {
      console.error('Error marking ride as reviewed:', err);
      showToast('Failed to mark ride as reviewed', 'error');
    } finally {
      setReviewingRide(false);
    }
  };

  const renderRideDetails = (ride) => (
    <Grid spacing={4} justifyContent='center'>

      <Grid container spacing={3} sx={{ mb: 2 }} justifyContent='center'>
        {/* Ride Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Ride Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1" color="text.secondary">
                    <strong>Ride Id</strong> #{ride.rideId}
                  </Typography>
                  <Typography variant="body1" color="text.secondary"><strong>Status: </strong>
                    <Chip
                      label={status}
                      color={getRideStatusColor(status)}
                      size="small"
                      sx={getRideStatusStyle(status)}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" color="text.secondary">
                    👥 <strong>Passengers:</strong> {ride.passengers || 1}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    🚗 <strong>Car Type:</strong> {carTypeNames[ride.carType] || `Type ${ride.carType}`}
                  </Typography>

                </Grid>
                <Grid item xs={12}>
                  {ride.callTime && (
                    <Typography variant="body1" color="text.secondary">
                      📞 <strong>Call Time:</strong> {formatDateTime(ride.callTime)}
                    </Typography>
                  )}
                  {ride.scheduledFor && (
                    <Typography variant="body1" color="text.secondary">
                      🗓️ <strong>Scheduled:</strong> {formatDateTime(ride.scheduledFor)}
                    </Typography>
                  )}
                  {ride.arrivedAtPickupTime && (
                    <Typography variant="body1" color="info.main">
                      📍 <strong>Arrived at Pickup:</strong> {formatDateTime(ride.arrivedAtPickupTime)}
                    </Typography>
                  )}
                  {ride.pickupTime && (
                    <Typography variant="body1  " color="success.main">
                      ✅ <strong>Picked Up:</strong> {formatDateTime(ride.pickupTime)}
                    </Typography>
                  )}
                  {ride.dropOffTime && (
                    <Typography variant="body1" color="success.main">
                      ✅ <strong>Dropped Off:</strong> {formatDateTime(ride.dropOffTime)}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Customer Information
              </Typography>
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary">
                  👨‍👩‍👧‍👦 <strong>Customer:</strong> {ride.customerName === '' ? 'N/A' : ride.customerName}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  ☎️ <strong>Phone #:</strong> {formatPhoneNumber(ride.customerPhoneNumber)}
                </Typography>
                {ride.flightNumber && (
                  <Typography variant="body1" color="text.secondary">
                    ✈️ <strong>Flight Number:</strong> {ride.flightNumber}
                  </Typography>
                )}
                <Typography variant="body1" color="text.secondary">
                  📝 <strong>Notes:</strong> {ride.notes === '' ? 'N/A' : ride.notes}
                </Typography>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 2 }} justifyContent='center'>
        {/* Driver Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Driver Information
              </Typography>
              <Grid item xs={12}>
                {((!ride.reassigned && ride.assignedToId) || ride.reassignedToId) ? (() => {
                  const car = ride.reassigned
                    ? ride.reassignedTo?.cars?.find(c => c.isPrimary)
                    : ride.assignedTo?.cars?.find(c => c.isPrimary);
                  return (
                    <Grid container spacing={3} sx={{ mb: 2 }}>

                      <Grid item xs={12}>
                        <Typography variant="body1" color="text.secondary">
                          🚘 <strong>Driver:</strong> #{ride.reassigned ? ride.reassignedToId : ride.assignedToId} - {ride.reassigned ? ride.reassignedTo?.name : ride.assignedTo?.name}
                        </Typography>
                        {(ride.reassigned ? ride.reassignedTo?.phoneNumber : ride.assignedTo?.phoneNumber) && (
                          <Typography variant="body1" color="text.secondary">
                            ☎️ <strong>Driver Phone #:</strong> {formatPhoneNumber(ride.reassigned ? ride.reassignedTo?.phoneNumber : ride.assignedTo?.phoneNumber)}
                          </Typography>
                        )}
                      </Grid>

                      {
                        (car &&
                          <Grid item xs={12}>
                            <Typography variant="body1" color="text.secondary">
                              🚗 <strong>Car:</strong> {car.color} {car.make} {car.model} ({car.year})
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              💺 <strong>Car Type:</strong>  {carTypeNames[car.type]} with {car.seats} seats
                            </Typography>
                          </Grid>
                        )
                      }
                    </Grid>
                  );
                })() : (
                  <Typography variant="body1" color="text.secondary">
                    🚘 <strong>Unassigned</strong>
                  </Typography>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Route Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6" color="primary">
                  Route Information
                </Typography>
                {ride.route?.roundTrip && (
                  <Chip
                    icon={<RepeatIcon />}
                    label="Round Trip"
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body1" color="text.secondary">
                    📍 <strong>Pickup:</strong> {ride.route?.pickup || 'N/A'}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    🏁 <strong>Dropoff:</strong> {ride.route?.dropOff || 'N/A'}
                  </Typography>

                </Box>
              </Grid>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Show stops and/or additional requirements */}
      {(ride.route?.stop1 || addOns.length > 0) && (
        <Grid container spacing={3} sx={{ mb: 2 }} justifyContent='center'>
          {ride.route?.stop1 && (
            <Grid item xs={12} md={addOns.length > 0 ? 6 : 12}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Additional Stops
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {ride.route?.stop1 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 1:</strong> {ride.route.stop1}
                      </Typography>
                    )}
                    {ride.route?.stop2 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 2:</strong> {ride.route.stop2}
                      </Typography>
                    )}
                    {ride.route?.stop3 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 3:</strong> {ride.route.stop3}
                      </Typography>
                    )}
                    {ride.route?.stop4 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 4:</strong> {ride.route.stop4}
                      </Typography>
                    )}
                    {ride.route?.stop5 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 5:</strong> {ride.route.stop5}
                      </Typography>
                    )}
                    {ride.route?.stop6 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 6:</strong> {ride.route.stop6}
                      </Typography>
                    )}
                    {ride.route?.stop7 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 7:</strong> {ride.route.stop7}
                      </Typography>
                    )}
                    {ride.route?.stop8 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 8:</strong> {ride.route.stop8}
                      </Typography>
                    )}
                    {ride.route?.stop9 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 9:</strong> {ride.route.stop9}
                      </Typography>
                    )}
                    {ride.route?.stop10 && (
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                        📌 <strong>Stop 10:</strong> {ride.route.stop10}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          {addOns.length > 0 && (
            <Grid item xs={12} md={ride.route?.stop1 ? 6 : 12}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    🧩 Additional Requirements
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 0.5 }}>
                    {addOns.map(a => {
                      const selected = ride.selectedAddOnIds
                        ? JSON.parse(ride.selectedAddOnIds || '[]').includes(a.id)
                        : false;
                      return (
                        <Typography key={a.id} variant="body1" color="text.secondary" sx={{ pl: 2 }}>
                          {selected ? '✅' : '❌'} {a.name}
                        </Typography>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
      <Grid container spacing={3} sx={{ mb: 2 }} justifyContent='center'>
        {/* Recurring Ride Information Card */}
        {ride.isRecurring && ride.recurring && (
          <Grid item xs={12}>

            <Card sx={{ border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <RepeatIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Recurring Ride Details
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body1" color="text.secondary">
                      📅 <strong>Day of Week:</strong> {DAYS_OF_WEEK_LONG[ride.recurring.dayOfWeek]}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" color="text.secondary">
                      ⏰ <strong>Time:</strong> {formatTimeOnly(ride.recurring.time)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" color="text.secondary">
                      🏁 <strong>End Date:</strong> {formatDate(ride.recurring.endDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Chip
                      icon={<RepeatIcon />}
                      label={new Date(ride.recurring.endDate) > new Date() ? "Active Series" : "Series Ended"}
                      color={new Date(ride.recurring.endDate) > new Date() ? "success" : "default"}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 2 }} justifyContent='center'>
        {/* Payment Information Card */}
        <Grid item xs={12} md={8} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                Payment Information
              </Typography>

              {(() => {
                const isCC = ride.paymentType === 'dispatcherCC' || ride.paymentType === 'driverCC';
                const ccFeeRate = PRICING_DEFAULTS.ccFeeRate;
                const baseCost = ride.cost || 0;
                const ccFee = isCC ? baseCost * ccFeeRate : 0;
                const totalDisplay = baseCost + ccFee;
                const companyProfit = baseCost - (ride.driversCompensation || 0);

                const stopCount = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                  .filter(n => ride.route?.[`stop${n}`]).length;
                const stopCost = settings.minStopCost ?? 10;
                // Use stored distance-based stops cost if available; fall back to flat for old rides.
                const stopsCost = ride.stopsCost != null ? ride.stopsCost : (stopCount * stopCost);
                const selectedIds = ride.selectedAddOnIds ? JSON.parse(ride.selectedAddOnIds) : [];
                const checkedAddOns = addOns.filter(a => selectedIds.includes(a.id));
                const addOnsFee = checkedAddOns.reduce((sum, a) => sum + a.fee, 0);
                const rushHourFee = pricingBreakdown?.rushHourSurcharge || 0;
                const baseFare = baseCost - (ride.waitTimeAmount || 0) - (ride.tip || 0) - stopsCost - addOnsFee - rushHourFee;
                // Use stored discount amount — never infer from account settings (discount may have been waived)
                const storedDiscount = ride.accountDiscountAmount || 0;
                const displayBaseFare = storedDiscount > 0 ? baseFare + storedDiscount : baseFare;

                const paymentTypeLabel = ride.paymentType === 'dispatcherCC' ? 'Card on File'
                  : ride.paymentType === 'driverCC' ? 'Driver CC'
                    : ride.paymentType === 'zelle' ? 'Zelle'
                      : ride.paymentType === 'cash' ? 'Cash'
                        : ride.paymentTokenId ? 'Card on File' : 'Cash';

                const statusChip = (() => {
                  if (ride.paymentType === 'zelle')
                    return <Chip icon={<CheckCircleIcon />} label="Paid (Zelle)" color="success" />;
                  if (ride.paymentTokenId) {
                    if (ride.cardCharged)
                      return <Chip icon={<CheckCircleIcon />} label="Paid (Card Charged)" color="success" />;
                    return <Chip icon={<CreditCardIcon />} label="Card on File (Pending)" color="info" />;
                  }
                  if (ride.paid)
                    return <Chip icon={<CheckCircleIcon />} label="Paid" color="success" />;
                  return <Chip icon={<WarningIcon />} label="Payment Required" color="warning" />;
                })();

                const labelStyle = { variant: 'body2', color: 'text.secondary' };
                const valueStyle = { variant: 'body2', color: 'text.primary', fontWeight: '600' };
                const rowStyle = { display: 'flex', justifyContent: 'space-between', mb: 0.75 };

                return (
                  <Grid container spacing={2}>
                    {/* LEFT — totals + payment info */}
                    <Grid item xs={12} sm={5}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                          <Typography variant="h5" color="success.main" fontWeight="bold" lineHeight={1.2}>
                            ${totalDisplay.toFixed(2)}
                          </Typography>
                          {isCC && (
                            <Typography variant="caption" color="text.secondary">incl. 4% CC fee</Typography>
                          )}
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Driver's Comp</Typography>
                          <Typography variant="h5" color="success.main" fontWeight="bold" lineHeight={1.2}>
                            ${(ride.driversCompensation || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Company Profit</Typography>
                          <Typography variant="h6" color="success.main" fontWeight="bold" lineHeight={1.2}>
                            ${companyProfit.toFixed(2)}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography {...labelStyle}>💳 Payment Type:</Typography>
                          <Typography {...valueStyle}>{paymentTypeLabel}</Typography>
                        </Box>
                        <Box sx={{ mt: 1 }}>{statusChip}</Box>
                      </Box>
                    </Grid>

                    {/* Vertical divider (desktop only) */}
                    <Grid item sm={1} sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
                      <Divider orientation="vertical" flexItem />
                    </Grid>
                    <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' } }}>
                      <Divider />
                    </Grid>

                    {/* RIGHT — breakdown */}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Breakdown
                      </Typography>
                      <Box sx={rowStyle}>
                        <Typography {...labelStyle}>Base Fare:</Typography>
                        <Typography {...valueStyle}>${displayBaseFare.toFixed(2)}</Typography>
                      </Box>
                      {storedDiscount > 0 && (
                        <Box sx={rowStyle}>
                          <Typography variant="body2" color="success.main">
                            {rideAccount
                              ? (rideAccount.discountType === 'percent'
                                ? `Discount (${rideAccount.discountValue}% — ${rideAccount.name})`
                                : `Discount ($${rideAccount.discountValue} off — ${rideAccount.name})`)
                              : 'Account Discount'}:
                          </Typography>
                          <Typography variant="body2" color="success.main" fontWeight="600">-${storedDiscount.toFixed(2)}</Typography>
                        </Box>
                      )}
                      {stopsCost > 0 && (
                        <Box sx={rowStyle}>
                          <Typography {...labelStyle}>
                            {ride.stopsCost != null
                              ? `Stops (${stopCount}×):`
                              : `Stops (${stopCount}× $${Math.round(stopCost)}):`}
                          </Typography>
                          <Typography {...valueStyle}>${Math.round(stopsCost)}</Typography>
                        </Box>
                      )}
                      {rushHourFee > 0 && (
                        <Box sx={rowStyle}>
                          <Typography {...labelStyle}>Rush Hour Fee:</Typography>
                          <Typography {...valueStyle}>${rushHourFee.toFixed(2)}</Typography>
                        </Box>
                      )}
                      {checkedAddOns.map(a => (
                        <Box key={a.id} sx={rowStyle}>
                          <Typography {...labelStyle}>{a.name}:</Typography>
                          <Typography {...valueStyle}>${a.fee.toFixed(2)}</Typography>
                        </Box>
                      ))}
                      <Box sx={rowStyle}>
                        <Typography {...labelStyle}>Wait Time:</Typography>
                        <Typography {...valueStyle}>${(ride.waitTimeAmount || 0).toFixed(2)}</Typography>
                      </Box>
                      <Box sx={rowStyle}>
                        <Typography {...labelStyle}>Tips:</Typography>
                        <Typography {...valueStyle}>${(ride.tip || 0).toFixed(2)}</Typography>
                      </Box>
                      {isCC && (
                        <Box sx={rowStyle}>
                          <Typography {...labelStyle}>CC Fee (4%):</Typography>
                          <Typography {...valueStyle}>${ccFee.toFixed(2)}</Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>
      </Grid>


    </Grid>
  );


  const renderDriverDetails = (driver) => {
    return (
      <Grid container spacing={3} justifyContent='center'>
        <Grid item xs={12} md={6}>
          {/* driver info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Driver Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body1" color="text.secondary">
                    <strong>Driver ID #{driver.id}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" color="text.secondary">
                    🙎‍♂️<strong>Name: {driver.name}</strong>
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    🗓️ <strong>Driving Since:</strong> {formatDate(driver.joinedDate)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1" color="text.secondary">
                    🛣️ <strong>Status: </strong>
                    <Chip
                      label={status}
                      color={getDriverStatusColor(status)}
                      size="small"
                      sx={getRideStatusStyle(status)}
                    />
                  </Typography>

                  <Typography variant="body1" color="text.secondary">
                    <strong>
                      {status === 'En-Route' ||
                        status === 'Driving'
                        ? '🚗 Currently Driving' : '⛔ Not Currently Driving'}
                    </strong>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          {/* contact info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body1" color="text.secondary">
                      📞 <strong>Phone</strong>
                    </Typography>
                  </Box>
                  <Typography variant="body1">{formatPhoneNumber(driver.phoneNumber)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body1" color="text.secondary">
                      📧 <strong>Email</strong>
                    </Typography>
                  </Box>
                  <Typography variant="body1">{driver.email || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {driver.chauffeurLicenseNumber && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">Chauffeur License</Typography>
                <Typography variant="body2" color="text.secondary">Chauffeur License #</Typography>
                <Typography variant="body1">{driver.chauffeurLicenseNumber}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} >
          {/* vehicle info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary" align="center">
                Vehicle Information
              </Typography>
              {driver.cars && driver.cars.length > 0 ? (
                <Grid container spacing={3} justifyContent='center'>
                  {driver.cars.map((car, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        elevation={car.isPrimary ? 3 : 1}
                        sx={{
                          p: 2,
                          pt: car.isPrimary ? 4 : 2,
                          border: car.isPrimary ? 2 : 0,
                          borderColor: car.isPrimary ? 'primary.main' : 'transparent',
                          position: 'relative'
                        }}
                      >
                        {car.isPrimary && (
                          <Chip
                            label="PRIMARY"
                            color="primary"
                            size="small"
                            sx={{ position: 'absolute', top: -10, right: 8 }}
                          />
                        )}
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography variant="body1" color="text.secondary">
                              🚘 <strong>Vehicle:</strong> {car.make || 'N/A'} {car.model || ''} {car.year ? `(${car.year})` : ''}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body1" color="text.secondary">
                              🪪 <strong>License Plate:</strong> {car.licensePlate || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body1" color="text.secondary">
                              🎨 <strong>Color:</strong> {car.color || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body1" color="text.secondary">
                              👥 <strong>Passengers:</strong> {car.seats || 1}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body1" color="text.secondary">
                              🚗 <strong>Car Type:</strong> {car.type || 'Car'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No vehicles registered
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleNotifyDriverToAddCar}
                    disabled={sendingMessage}
                  >
                    {sendingMessage ? 'Sending...' : 'Notify Driver to Add Vehicle'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid >
    )
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading {itemType} details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          onClick={onBackToList}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
        >
          Back to List
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button
            onClick={onBackToList}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to List
          </Button>
          <Box display="flex" alignItems="center" gap={1}>
            {itemType === 'ride' ? <CarIcon /> : <PersonIcon />}
            <Typography variant="h5">
              {itemType === 'ride' ? `Ride Id #${item?.rideId}` : `Driver: ${item?.name}`}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {isReviewMode && (
              <>
                <Chip
                  label={`Reviewing ${currentIndex} of ${totalUnreviewed}`}
                  color="info"
                  variant="outlined"
                />
                <Button
                  onClick={handleReviewAndNext}
                  endIcon={reviewingRide ? <CircularProgress size={16} color="inherit" /> : <NavigateNextIcon />}
                  variant="contained"
                  color="success"
                  disabled={reviewingRide}
                >
                  {reviewingRide ? 'Marking...' : 'Mark Reviewed & Next'}
                </Button>
              </>
            )}
            <Button
              onClick={loadItemDetails}
              startIcon={<RefreshIcon />}
              variant="contained"
              color="primary"
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {itemType === 'ride' && item && renderRideDetails(item)}
      {itemType === 'driver' && item && renderDriverDetails(item)}
    </Box>
  );
};

export default DetailView;